import "mathlive";
import "./mathfield-overrides.css";
import { MathfieldElement } from "mathlive";
// biome-ignore lint/correctness/noUnusedImports:
import React, { ComponentProps, useRef } from "react";
import { MathfieldCanceBtn, MathfieldDialogTitle, MathfieldInsertBtn } from "./MathfieldStyled";
import { getMathfieldDialogEl, getMathfieldDialogId, getMathfieldInputEl, getMathfieldInputId } from "./utils";

declare global {
    namespace React.JSX {
        interface IntrinsicElements {
            "math-field": React.DetailedHTMLProps<React.HTMLAttributes<MathfieldElement>, MathfieldElement>;
        }
    }
}
declare module "mathlive" {
    interface MathfieldElement {
        /**
         * I want it to be able to set the initial value, so that when we close the popup we can run the sanity check
         * with this `initialValue`
         */
        initialValue: string;
    }
}

const MathfieldDialog = ({
    onInsert,
    mathfieldId,
}: { onInsert: (p: { expression: string }) => void; mathfieldId: string }) => {
    const mfRef = useRef<MathfieldElement>(null);

    return (
        <div
            id={getMathfieldDialogId({ mathfieldId })}
            style={{
                display: "none",
                position: "fixed",
                top: 0,
                left: 0,
                zIndex: 9999,
                width: "100vw",
                height: "100vh",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
            }}>
            {/* BACKDROP */}
            <div
                style={{
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    position: "absolute",
                    height: "100vh",
                    width: "100vw",
                    backgroundColor: "#000000",
                    opacity: 0.5,
                    zIndex: 9998,
                }}
            />

            <div
                style={{
                    zIndex: 9999,
                    display: "flex",
                    backgroundColor: "var(--color-background)",
                    maxWidth: "calc(100vw - 20px)",
                    flexDirection: "column",
                    marginTop: "-10rem",
                    height: "fit-content",
                    padding: "20px",
                    borderRadius: "20px",
                    boxSizing: "border-box",
                }}>
                <MathfieldDialogTitle style={{ marginBottom: "16px", alignSelf: "flex-start" }}>
                    Insert Your Equation
                </MathfieldDialogTitle>

                <math-field
                    style={{
                        width: "600px",
                        maxWidth: "100%",
                        outline: "none",
                        borderRadius: "12px",
                        padding: "2px 0px",
                        borderColor: "var(--color-neutral-1)",
                        backgroundColor: "transparent",
                        color: "var(--color-neutral-black)",
                    }}
                    id={getMathfieldInputId({ mathfieldId })}
                    ref={el => {
                        if (el === null) {
                            return;
                        }
                        el.placeholder = "\\text{Enter your equation here}";
                        el.mathVirtualKeyboardPolicy = "manual";
                        mfRef.current = el;
                    }}
                />

                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignSelf: "flex-end" }}>
                    {/* NOTE: basically, we don't have close/cancel btn functinoality, we will always make sure to run our sanity check after the popup closed. */}
                    {/* so, close/cancel btn acts like insert btn but with the `expression` passed is the initial value */}
                    <MathfieldCanceBtn
                        style={{ marginLeft: "auto", marginTop: "16px", fontSize: "14px" }}
                        onClick={() => {
                            if (!mfRef.current) {
                                closeMathfieldDialog({ mathfieldId: mathfieldId });
                                return;
                            }
                            onInsert({ expression: mfRef.current?.initialValue });
                            closeMathfieldDialog({ mathfieldId: mathfieldId });
                        }}
                    />
                    <MathfieldInsertBtn
                        style={{ marginLeft: "auto", marginTop: "16px" }}
                        onClick={() => {
                            onInsert({ expression: mfRef.current?.value || "" });
                            closeMathfieldDialog({ mathfieldId: mathfieldId });
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export const renderMathfieldDialog = ({ onInsert, mathfieldId }: ComponentProps<typeof MathfieldDialog>) => {
    return <MathfieldDialog onInsert={onInsert} mathfieldId={mathfieldId} />;
};

export const openMathfieldDialog = ({ selectedLatex, mathfieldId }: { selectedLatex: string; mathfieldId: string }) => {
    const dialog = getMathfieldDialogEl({ mathfieldId });
    const mf = getMathfieldInputEl({ mathfieldId });

    if (dialog && mf) {
        mf.value = selectedLatex;
        mf.initialValue = selectedLatex;
        mf.focus();
        window.mathVirtualKeyboard.show();
        dialog.style.display = "flex";
    }
};

export const closeMathfieldDialog = ({ mathfieldId }: { mathfieldId: string }) => {
    const dialog = getMathfieldDialogEl({ mathfieldId });
    if (dialog) {
        window.mathVirtualKeyboard.hide();
        dialog.style.display = "none";
    }
};
